/*!
 * tpo-swatch-a11y.js — v1.1
 * Accessible multi-select colour swatches for Easify / TPO Product Options.
 * Progressive enhancement — every selection limit is resolved at runtime.
 *
 * v1.1 fixes
 *  1. A disabled quantity_selector is no longer trusted. Easify keeps a stale
 *     `limitTotalSelected` on options whose stepper was switched off, and that
 *     dead 10 was being used as the requirement for a 4-swatch group
 *     ("Quantity becomes available once you have selected 10 colors").
 *  2. Falls back to the option's own multi_select / min_value / max_value.
 *  3. The requirement is clamped to the number of swatches actually rendered,
 *     so no message can ever ask for more than a shopper is able to pick.
 *  4. One shared quantity gate for all groups. Previously each group wrote to
 *     the same quantity input and the last one to sync won, which left the
 *     field read-only (and swallowed "+" clicks) even when every requirement
 *     was met.
 *  5. Correct singular/plural, plus a per-group unit noun so finishers and
 *     included accessories are not described as "colors".
 *
 * Optional config — set BEFORE this file loads:
 *   window.TPO_SWATCH_A11Y_CONFIG = {
 *     unit: 'colors',                    // or 'color|colors'
 *     stepperTabStops: 'selected-only',  // or 'never'
 *     unitByOption: { '<easify-option-id>': 'item|items' }
 *   };
 * Per-group override without JS: data-a11y-unit="item|items" on .tpo_option-container
 */
(function () {
  'use strict';

  var CFG = Object.assign({
    unit: 'colors',
    stepperTabStops: 'selected-only',
    unitByOption: {}
  }, window.TPO_SWATCH_A11Y_CONFIG || {});

  var SEL = {
    group: '.tpo_option-type-image-swatches-wrapper',
    box: '.tpo_option-container',
    item: '.tpo_swatches-label_position',
    input: '.tpo_color-swatches-input',
    hit: '.tpo_image-wrapper',
    name: '.tpo_option_label',
    price: '.tpo_additional-price',
    caption: '.tpo_option_type_label_wrapper',
    onCls: 'tpo_is_checked_swatches',
    limit: '.tpo_quantity_selector_total-limit',
    counter: '.tpo_quantity_selector_total-selected',
    totals: '.tpo_quantity_selector_total-wrapper',
    totalsBody: '.tpo_quantity_selector_total-content',
    help: '.tpo_option-help-text',
    optLabel: '.tpo_option-label',
    reqStar: '.tpo_option-required-indicator',
    nativeErr: '.tpo_error-message',
    hiddenProxy: '.tpo_hidden-checkbox',
    stepper: '.tpo_quantity-selector_wrapper',
    stepInput: '.tpo_quantity-selector_input',
    minus: '.tpo_quantity-selector_minus',
    plus: '.tpo_quantity-selector_plus',
    qty: 'input[name="quantity"]:not(.tpo_quantity-selector_input)',
    qtyBlock: '.quantity-block',
    atc: '.tpo_add-to-cart, button[name="add"]'
  };

  /* A single hint node shared by every group — fix #4. */
  var HINT_ID = 'tpo-a11y-qty-hint';

  /* Options that are not colours. Easify option ids are stable across option
     sets, so one entry covers every product that reuses the option. */
  var UNIT_BY_OPTION = {
    '92b99068-1cd7-4d23-bc8c-50523b10b6bc': 'finish|finishes',   // Choose One 4-Coat
    'b5c34a81-6a6e-447a-aa14-84c820a7bcda': 'item|items'         // Included With Your Kit:
  };

  var registry = [], uid = 0, live = null, delegated = false;

  /* ----------------------------------------------------------------- utils */

  function txt(el) {
    return el ? String(el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function num(v) {
    var m = String(v == null ? '' : v).match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
  }

  function toInt(v) {
    var n = parseInt(v, 10);
    return isFinite(n) && n > 0 ? n : 0;
  }

  function singular(w) {
    if (/ies$/i.test(w)) return w.replace(/ies$/i, 'y');
    if (/(ch|sh|ss|x|z)es$/i.test(w)) return w.replace(/es$/i, '');
    if (/ss$/i.test(w)) return w;
    if (/s$/i.test(w)) return w.replace(/s$/i, '');
    return w;
  }

  /* Accepts 'colors' or 'singular|plural'. A lone form is treated as the
     plural, which keeps an existing CFG.unit working unchanged — fix #5. */
  function unitWord(spec, n) {
    var raw = String(spec || CFG.unit || 'colors').trim(), p = raw.split('|');
    var one = p.length > 1 ? p[0].trim() : singular(raw);
    var many = p.length > 1 ? p[1].trim() : raw;
    return Math.abs(n) === 1 ? (one || many) : (many || one);
  }

  function unitOf(G) {
    var id = String(G.box.id || '').split(',')[1] || '';
    return G.box.getAttribute('data-a11y-unit') ||
           ((CFG.unitByOption || {})[id]) ||
           UNIT_BY_OPTION[id] || CFG.unit;
  }

  function labelOf(G) {
    return txt(G.box.querySelector(SEL.optLabel)).replace(/[\s*:]+$/, '');
  }

  function liveRegion() {
    if (!live || !live.isConnected) {
      live = document.createElement('div');
      live.className = 'tpo-a11y-live tpo-a11y-sr';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      document.body.appendChild(live);
    }
    return live;
  }

  function announce(msg) {
    var el = liveRegion();
    clearTimeout(el.__t);
    el.__t = setTimeout(function () { el.textContent = ''; el.textContent = msg; }, 200);
  }

  /* Easify listens for real pointer sequences, so replay one. */
  function press(el) {
    if (!el) return;
    ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach(function (type) {
      var C = type.indexOf('pointer') === 0 && window.PointerEvent ? PointerEvent : MouseEvent;
      el.dispatchEvent(new C(type, {
        bubbles: true, cancelable: true, composed: true, view: window,
        button: 0, buttons: 1, isPrimary: true, detail: 1
      }));
    });
  }

  /* ------------------------------------------------- global gate readiness */

  /* Every group that is not satisfied right now. The quantity field is shared,
     so readiness has to be decided across all of them, not per group. */
  function pendingGroups() {
    var out = [];
    registry.forEach(function (G) {
      if (!G.group.isConnected) return;
      var L = G.limit(), n = G.count();
      if (L && (n < L.min || n > L.max)) out.push({ G: G, L: L, n: n });
    });
    return out;
  }

  function gateMessage(pending) {
    function need(x) { return x.L.exact ? x.L.max : x.L.min; }

    if (pending.length === 1) {
      var x = pending[0], t = need(x);
      return 'Quantity becomes available once you have selected ' + (x.L.exact ? '' : 'at least ') +
             t + ' ' + unitWord(unitOf(x.G), t) + '. ' + x.n + ' of ' + t + ' selected.';
    }
    return 'Quantity becomes available once these selections are complete: ' +
      pending.map(function (x) {
        var t = need(x);
        return labelOf(x.G) + ' — ' + x.n + ' of ' + t + ' ' + unitWord(unitOf(x.G), t) + ' selected';
      }).join('; ') + '.';
  }

  /* ---------------------------------------------------------------- a group */

  function Group(group) {
    var box = group.closest(SEL.box) || group.parentElement;
    var gid = 'tpo-swatch-a11y-' + (++uid);
    var api = { group: group, box: box, id: gid };
    var entries = [];
    var statusEl = null, errEl = null;
    var instrId = gid + '-instr';
    var lastCount = null, muted = false, errKind = null;
    var optionCache = null;
    var optionId = String(box.id || '').split(',')[1] || '';

    /* ---------- how many must be chosen ---------- */

    function optionRecord() {
      if (optionCache) return optionCache;
      if (!optionId) return null;
      try {
        var sets = (window.TPOConfigs || {}).product_options_easify || [];
        var found = null;
        sets.forEach(function (set) {
          (((set || {}).option_set || {}).options || []).forEach(function (o) {
            if (!found && String(o.id) === optionId) found = o;
          });
        });
        if (found) optionCache = found;
        return found;
      } catch (e) {
        return null;
      }
    }

    function fromAppConfig() {
      var o = optionRecord();
      if (!o) return null;

      var qs = o.quantity_selector;
      if (typeof qs === 'string') {
        try { qs = JSON.parse(qs); } catch (e) { qs = null; }
      }
      var c = (qs && qs.configs) || null;

      /* Stepper totals count ONLY while the stepper feature is enabled. A
         switched-off stepper can still carry an obsolete limitTotalSelected
         (e.g. 10 on a 4-swatch group) — fix #1. */
      if (c && qs.enable === true && c.hasLimitations !== false) {
        if (c.isUseMinMaxSelected && toInt(c.minValueTotal)) {
          var lo = toInt(c.minValueTotal);
          var hi = toInt(c.maxValueTotal) || lo;
          return { min: lo, max: hi, exact: lo === hi };
        }
        var lim = toInt(c.limitTotalSelected);
        if (lim) return { min: lim, max: lim, exact: true };
      }

      /* Otherwise the option's own rules are authoritative — fix #2. */
      if (!toInt(o.multi_select)) {
        var required = toInt(o.is_required) ? 1 : 0;
        return { min: required, max: 1, exact: true };
      }

      var min = toInt(o.min_value), max = toInt(o.max_value);
      if (min || max) {
        min = min || max;
        max = max || min;
        return { min: min, max: max, exact: min === max };
      }
      return null;
    }

    function resolveLimit() {
      /* 1. Easify's own rendered limit badge is the most trustworthy source. */
      var exact = num(txt(box.querySelector(SEL.limit)));
      if (exact) return { min: exact, max: exact, exact: true };

      /* 2. A min/max totals widget renders two numbers. */
      var body = box.querySelector(SEL.totalsBody);
      if (body && !/exact/i.test(body.className || '')) {
        var ns = (txt(body).match(/\d+/g) || []).map(Number).filter(function (v) { return v > 0; });
        if (ns.length > 1) {
          return { min: Math.min.apply(null, ns), max: Math.max.apply(null, ns), exact: false };
        }
      }

      /* 3. The app's option record. */
      var cfg = fromAppConfig();
      if (cfg) return cfg;

      /* 4. Last resort: a number written into the label or help text. */
      var n = num(txt(box.querySelector(SEL.optLabel)) + ' ' + txt(box.querySelector(SEL.help)));
      return n ? { min: n, max: n, exact: true } : null;
    }

    /* You can never be asked for more than exists on screen — fix #3. */
    function clamp(L) {
      var avail = entries.length;
      if (!L || !avail) return L;
      var min = Math.min(L.min, avail);
      var max = Math.min(L.max, avail);
      if (min > max) min = max;
      return {
        min: min, max: max,
        exact: L.exact || min === max,
        clamped: min !== L.min || max !== L.max
      };
    }

    function limit() { return clamp(resolveLimit()); }

    function isOn(e) { return !!(e.label && e.label.classList.contains(SEL.onCls)); }

    function count() {
      var n = 0;
      entries.forEach(function (e) { if (isOn(e)) n++; });
      return n;
    }

    function requirement() {
      var L = limit(), n = count();
      return {
        id: gid, label: labelOf(api), unit: unitOf(api), L: L, n: n,
        ready: !L || (n >= L.min && n <= L.max)
      };
    }

    /* ---------- build / rebuild: idempotent, survives TPO re-renders ---------- */

    function build() {
      entries = [].slice.call(group.querySelectorAll(SEL.item)).map(function (item, i) {
        var input = item.querySelector(SEL.input); if (!input) return null;
        var label = input.closest('label') || item.querySelector('label');
        var name = txt(item.querySelector(SEL.name)) || String(input.value || '').trim() || ('Colour ' + (i + 1));
        var price = txt(item.querySelector(SEL.price));
        if (!input.id) input.id = gid + '-opt-' + i;
        input.classList.add('tpo-a11y-input');                 // focusable, visually hidden
        input.setAttribute('aria-label', price ? (name + ', ' + price) : name);
        if (label) label.classList.add('tpo-a11y-swatch');
        // swatch art is decorative; alt used to contain the raw image URL
        [].forEach.call(item.querySelectorAll('img'), function (img) {
          img.alt = ''; img.setAttribute('aria-hidden', 'true');
        });
        [].forEach.call(item.querySelectorAll('.tpo_image-swatches-tooltip'), function (t) {
          t.setAttribute('aria-hidden', 'true');
        });
        return {
          item: item, input: input, label: label, name: name,
          hit: item.querySelector(SEL.hit) || label, index: i
        };
      }).filter(Boolean);
      entries.forEach(function (e, i) { e.index = i; });
    }

    function regions() {
      var lab = box.querySelector(SEL.optLabel + ' .form__label') || box.querySelector(SEL.optLabel);
      if (lab && !lab.id) lab.id = gid + '-label';

      var star = box.querySelector(SEL.reqStar);
      if (star) star.setAttribute('aria-hidden', 'true');       // aria-required conveys this

      var help = box.querySelector(SEL.help);
      if (help && !help.id) help.id = gid + '-help';

      var totals = box.querySelector(SEL.totals);
      if (totals) totals.setAttribute('aria-hidden', 'true');

      var ne = box.querySelector(SEL.nativeErr);
      if (ne) ne.setAttribute('aria-hidden', 'true');

      [].forEach.call(box.querySelectorAll(SEL.hiddenProxy), function (p) {
        p.tabIndex = -1; p.setAttribute('aria-hidden', 'true'); // TPO's hidden validation proxy
      });

      statusEl = box.querySelector('.tpo-a11y-status');
      if (!statusEl || !statusEl.isConnected) {
        statusEl = document.createElement('p');
        statusEl.id = gid + '-status';
        statusEl.className = 'tpo-a11y-status';
        if (totals && totals.parentNode) totals.parentNode.insertBefore(statusEl, totals.nextSibling);
        else group.parentNode.insertBefore(statusEl, group.nextSibling);
      }

      errEl = box.querySelector('.tpo-a11y-error');
      if (!errEl || !errEl.isConnected) {
        errEl = document.createElement('p');
        errEl.id = gid + '-error';
        errEl.className = 'tpo-a11y-error';
        errEl.setAttribute('role', 'alert');
        errEl.hidden = true;
        statusEl.parentNode.insertBefore(errEl, statusEl.nextSibling);
      }

      if (!document.getElementById(instrId)) {
        var instr = document.createElement('p');
        instr.id = instrId;
        instr.className = 'tpo-a11y-sr';
        instr.textContent = 'Use Tab or the arrow keys to move between colours. Press Space to select or deselect a colour.';
        group.parentNode.insertBefore(instr, group);
      }

      group.setAttribute('role', 'group');
      if (lab) group.setAttribute('aria-labelledby', lab.id);
      describe();
    }

    function describe() {
      var help = box.querySelector(SEL.help), ids = [];
      if (errEl && !errEl.hidden) ids.push(errEl.id);
      ids.push(statusEl.id);
      if (help && help.id) ids.push(help.id);
      ids.push(instrId);
      group.setAttribute('aria-describedby', ids.join(' '));
    }

    /* ---------- activation bridge + limit guard ---------- */

    function toggle(entry) {
      if (!entry) return false;
      var L = limit();
      if (L && !isOn(entry) && count() >= L.max) {
        var u = unitWord(unitOf(api), L.max);
        showError('max', 'You can choose only ' + L.max + ' ' + u + '. Deselect a colour before choosing another one.');
        announce('Limit reached: ' + L.max + ' ' + u + ' already selected. Deselect one before choosing another.');
        return false;
      }
      muted = true;
      press(entry.hit);
      setTimeout(function () { muted = false; sync(); }, 0);
      return true;
    }

    /* ---------- keyboard: native Tab order + grid arrows + type-ahead ---------- */

    function columns() {
      if (entries.length < 2) return 1;
      var top = entries[0].item.getBoundingClientRect().top, n = 0;
      for (var i = 0; i < entries.length; i++) {
        var r = entries[i].item.getBoundingClientRect();
        if (Math.abs(r.top - top) < 4) n++; else break;
      }
      return n || 1;
    }

    function focusAt(i) {
      if (!entries.length) return;
      entries[Math.max(0, Math.min(entries.length - 1, i))].input.focus();
    }

    var buf = '', bufTimer;

    function onKeyDown(ev, entry) {
      var cur = entry.index, cols = columns(), next = null, k = ev.key;

      if (k === ' ' || k === 'Spacebar' || k === 'Enter') { ev.preventDefault(); toggle(entry); return; }
      if (k === 'ArrowRight') next = cur + 1;
      else if (k === 'ArrowLeft') next = cur - 1;
      else if (k === 'ArrowDown') next = cur + cols;
      else if (k === 'ArrowUp') next = cur - cols;
      else if (k === 'Home') next = ev.ctrlKey ? 0 : cur - (cur % cols);
      else if (k === 'End') next = ev.ctrlKey ? entries.length - 1 : cur - (cur % cols) + cols - 1;
      else if (k && k.length === 1 && /[a-z0-9]/i.test(k) && !ev.metaKey && !ev.ctrlKey && !ev.altKey) {
        buf += k.toLowerCase();
        clearTimeout(bufTimer);
        bufTimer = setTimeout(function () { buf = ''; }, 600);
        for (var s = 1; s <= entries.length; s++) {
          var j = (cur + s) % entries.length;
          if (entries[j].name.toLowerCase().indexOf(buf) === 0) { focusAt(j); return; }
        }
        return;
      }
      if (next !== null) { ev.preventDefault(); focusAt(next); }
    }

    /* ---------- product quantity gating (shared across groups) ---------- */

    function qtyParts() {
      var input = document.querySelector(SEL.qty); if (!input) return null;
      var block = input.closest(SEL.qtyBlock) || input.parentElement;
      return { input: input, block: block, buttons: [].slice.call(block.querySelectorAll('button')) };
    }

    function gate() {
      var p = qtyParts(); if (!p) return;

      var pending = pendingGroups();
      var ready = pending.length === 0;

      var hint = document.getElementById(HINT_ID);
      if (!hint) {
        hint = document.createElement('p');
        hint.id = HINT_ID;
        hint.className = 'tpo-a11y-qty-hint';
      }
      if (!hint.isConnected) p.block.parentNode.insertBefore(hint, p.block);

      if (!p.input.getAttribute('aria-label')) p.input.setAttribute('aria-label', 'Quantity');
      p.input.setAttribute('aria-describedby', HINT_ID);
      p.input.setAttribute('aria-disabled', String(!ready));    // announced, still discoverable
      p.input.readOnly = !ready;
      p.block.classList.toggle('tpo-a11y-disabled', !ready);

      p.buttons.forEach(function (b, i) {
        if (!b.getAttribute('aria-label')) {
          b.setAttribute('aria-label', i === 0 ? 'Decrease quantity' : 'Increase quantity');
        }
        if (!ready) {
          b.setAttribute('aria-disabled', 'true');
          b.classList.add('tpo-a11y-inert');
          b.setAttribute('data-a11y-gated', '1');
        } else if (b.getAttribute('data-a11y-gated')) {
          b.removeAttribute('aria-disabled');
          b.classList.remove('tpo-a11y-inert');
          b.removeAttribute('data-a11y-gated');
        }
      });

      var text = ready ? '' : gateMessage(pending);
      if (hint.textContent !== text) hint.textContent = text;
      hint.hidden = ready;

      if (!p.block.__a11yGate) {                                // make the disabled state real
        p.block.__a11yGate = 1;
        ['click', 'keydown', 'beforeinput', 'paste', 'wheel'].forEach(function (type) {
          p.block.addEventListener(type, function (ev) {
            if (p.input.getAttribute('aria-disabled') !== 'true') return;
            if (type === 'keydown' && ['Tab', 'Shift', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(ev.key) > -1) return;
            ev.preventDefault(); ev.stopPropagation();
            var h = document.getElementById(HINT_ID);
            if (h && h.textContent) announce(h.textContent);
          }, true);
        });
      }
    }

    /* ---------- per-swatch steppers: named buttons, no dead tab stops ---------- */

    function steppers() {
      entries.forEach(function (e) {
        var st = e.item.querySelector(SEL.stepper); if (!st) return;
        var on = isOn(e), tabbable = (CFG.stepperTabStops === 'never') ? false : on;
        var q = st.querySelector(SEL.stepInput);
        if (q) { q.setAttribute('aria-label', 'Quantity of ' + e.name); q.tabIndex = tabbable ? 0 : -1; }
        [[st.querySelector(SEL.minus), 'Decrease quantity of '],
         [st.querySelector(SEL.plus), 'Increase quantity of ']].forEach(function (pair) {
          var el = pair[0]; if (!el) return;
          el.setAttribute('role', 'button');
          el.setAttribute('aria-label', pair[1] + e.name);
          el.tabIndex = tabbable ? 0 : -1;
          if (!el.__a11yKeys) {
            el.__a11yKeys = 1;
            el.addEventListener('keydown', function (ev) {
              if (ev.key === ' ' || ev.key === 'Enter' || ev.key === 'Spacebar') { ev.preventDefault(); press(el); }
            });
          }
        });
        st.setAttribute('aria-hidden', String(!on));
      });
    }

    /* ---------- messaging ---------- */

    function progress(n, L) {
      var u = unitOf(api);
      if (!L) return n + ' ' + unitWord(u, n) + ' selected.';
      if (L.exact) return n + ' of ' + L.max + ' ' + unitWord(u, L.max) + ' selected.';
      if (L.min === 0) return n + ' ' + unitWord(u, n) + ' selected. Choose up to ' + L.max + '.';
      return n + ' ' + unitWord(u, n) + ' selected. Choose between ' + L.min + ' and ' + L.max + '.';
    }

    function showError(kind, msg) {
      if (!errEl) regions();
      errKind = kind;
      errEl.hidden = false;
      errEl.textContent = msg;
      if (kind === 'min') {
        group.setAttribute('aria-invalid', 'true');
        entries.forEach(function (e) { e.input.setAttribute('aria-invalid', 'true'); });
      }
      describe();
    }

    function clearError() {
      errKind = null;
      if (errEl && !errEl.hidden) { errEl.hidden = true; errEl.textContent = ''; }
      group.removeAttribute('aria-invalid');
      entries.forEach(function (e) { e.input.removeAttribute('aria-invalid'); });
      describe();
    }

    /* ---------- mirror widget state into ARIA ---------- */

    function sync() {
      if (!statusEl || !statusEl.isConnected) regions();

      var L = limit(), n = count();
      var ready = !L || (n >= L.min && n <= L.max);

      entries.forEach(function (e) {
        var on = isOn(e);
        if (e.input.checked !== on) e.input.checked = on;       // programmatic selected state
        e.item.setAttribute('data-selected', String(on));
      });

      group.setAttribute('data-a11y-limit', L ? L.max : '');
      group.setAttribute('data-a11y-selected', n);
      group.setAttribute('aria-required', String(!!(L && L.min > 0)));
      statusEl.textContent = progress(n, L);

      gate();
      steppers();

      var changed = (lastCount !== null && n !== lastCount);
      if (errKind === 'min' && (ready || changed)) clearError();
      if (errKind === 'max' && L && n < L.max) clearError();

      if (changed) {
        var msg = progress(n, L);
        if (ready && L && L.exact) {
          msg += pendingGroups().length === 0
            ? ' Selection complete. Quantity is now available.'
            : ' Selection complete.';
        } else if (L && n >= L.max) {
          msg += ' Maximum reached.';
        }
        announce(msg);
      }
      lastCount = n;
    }

    /* ---------- public surface ---------- */

    api.init = function () {
      build(); regions();

      var t = null;
      new MutationObserver(function () {
        if (muted) return;
        clearTimeout(t);
        t = setTimeout(function () { if (group.isConnected) { build(); regions(); sync(); } }, 50);
      }).observe(group, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

     var counter = box.querySelector(SEL.counter);
      if (counter) {
        new MutationObserver(function () { sync(); })
          .observe(counter, { childList: true, characterData: true, subtree: true });
      }

      sync();
      lastCount = count();
      group.setAttribute('data-a11y-enhanced', 'true');
      return api;
    };

    api.entryFor = function (node) {
      var item = node && node.closest ? node.closest(SEL.item) : null;
      if (!item) return null;
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].item === item) return entries[i];
      }
      return null;
    };

    api.onKeyDown   = onKeyDown;
    api.toggle      = toggle;
    api.limit       = limit;          // used by pendingGroups()
    api.count       = count;          // used by pendingGroups()
    api.sync        = sync;
    api.requirement = requirement;
    api.refreshGate = gate;           // lets boot() re-evaluate after a group is removed
    api.entries     = function () { return entries; };
    api.showError   = showError;
    api.clearError  = clearError;
    return api;
  }

  /* ------------------------------------------------------------- delegation */

  function groupFor(node) {
    var g = node && node.closest ? node.closest(SEL.group) : null;
    if (!g) return null;
    for (var i = 0; i < registry.length; i++) {
      if (registry[i].group === g) return registry[i];
    }
    return null;
  }

  function delegate() {
    if (delegated) return;
    delegated = true;

    /* Keyboard on the visually hidden checkboxes. */
    document.addEventListener('keydown', function (ev) {
      var t = ev.target;
      if (!t || !t.matches || !t.matches(SEL.input)) return;
      var G = groupFor(t); if (!G) return;
      var e = G.entryFor(t);
      if (e) G.onKeyDown(ev, e);
    }, true);

    /* Clicking the caption under a swatch toggles it. */
    document.addEventListener('click', function (ev) {
      var cap = ev.target.closest ? ev.target.closest(SEL.caption) : null;
      if (!cap || ev.target.closest('a,button,[role="button"],' + SEL.stepper)) return;
      var G = groupFor(cap); if (!G) return;
      var e = G.entryFor(cap);
      if (e) { ev.preventDefault(); G.toggle(e); e.input.focus(); }
    }, false);

    /* Add to cart: surface every unmet requirement with the right numbers. */
    document.addEventListener('click', function (ev) {
      if (!ev.target.closest || !ev.target.closest(SEL.atc)) return;
      var firstBad = null;
      registry.forEach(function (G) {
        if (!G.group.isConnected) return;
        var L = G.limit(), n = G.count();
        if (!L || n >= L.min) return;
        var need = L.exact ? L.max : L.min;
        G.showError('min', 'Please choose ' + (L.exact ? need : L.min + ' to ' + L.max) + ' ' +
          unitWord(unitOf(G), need) + ' before adding to cart. You have chosen ' + n + '.');
        if (!firstBad) firstBad = G;
      });
      if (firstBad) {
        var first = firstBad.entries()[0];
        if (first) first.input.focus();
      }
    }, true);
  }

  /* -------------------------------------------------------------------- css */

  var CSS = [
    '.tpo-a11y-input{position:absolute!important;top:0!important;left:0!important;width:1.25rem!important;height:1.25rem!important;margin:0!important;padding:0!important;border:0!important;opacity:0!important;visibility:visible!important;pointer-events:none!important;z-index:1}',
    '.tpo-a11y-swatch{position:relative;display:block;cursor:pointer}',
    '.tpo-a11y-swatch:focus-within{outline:3px solid #0b57d0;outline-offset:3px;border-radius:.25rem;box-shadow:0 0 0 6px rgba(11,87,208,.2)}',
    '.tpo_is_checked_swatches::after{content:"";position:absolute;top:.25rem;right:.25rem;width:1.15rem;height:1.15rem;border-radius:50%;background-color:#111;box-shadow:0 0 0 2px #fff;background-repeat:no-repeat;background-position:center;background-size:.7rem .7rem;background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'M3 8.4l3.2 3.2L13 4.8\'/%3E%3C/svg%3E");pointer-events:none}',
    '.tpo-a11y-status{margin:.35rem 0;font-size:1rem;line-height:1.4;font-weight:600;color:#1a1a1a ; display:none;}',
    '.tpo-a11y-error{margin:.35rem 0;font-size:.9375rem;line-height:1.45;font-weight:600;color:#9e1006}',
    '.tpo-a11y-error[hidden],.tpo-a11y-qty-hint[hidden]{display:none}',
    '.tpo-a11y-qty-hint{margin:.35rem 0;font-size:.9375rem;line-height:1.45;color:#54595f; display:none;}',
    '.tpo-a11y-disabled{opacity:.6}',
    '.tpo-a11y-disabled input,.tpo-a11y-inert{cursor:not-allowed}',
    '.tpo-a11y-sr{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0 0 0 0)!important;clip-path:inset(50%)!important;white-space:nowrap!important;border:0!important}',
    '.tpo_option-help-text{color:#54595f!important}',
    '.tpo_error-message{font-size:.875rem!important;color:#9e1006!important}',
    '.tpo_option_type_label_wrapper{cursor:pointer}',
    'input[name="quantity"]:focus-visible,.tpo_quantity-selector_input:focus-visible,.quantity-block button:focus-visible,.tpo_quantity-selector_minus:focus-visible,.tpo_quantity-selector_plus:focus-visible{outline:3px solid #0b57d0!important;outline-offset:2px!important}',
    '@media (forced-colors:active){.tpo-a11y-swatch:focus-within{outline:3px solid Highlight}.tpo_is_checked_swatches::after{background-color:Highlight;forced-color-adjust:none}}'
  ].join('\n');

  function styles() {
    if (document.getElementById('tpo-swatch-a11y-css')) return;
    var s = document.createElement('style');
    s.id = 'tpo-swatch-a11y-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* ------------------------------------------------------------------- boot */

  function boot() {
    styles();
    delegate();

    var before = registry.length;
    registry = registry.filter(function (G) { return G.group.isConnected; });
    var removed = registry.length !== before;

    var added = 0;
    [].forEach.call(document.querySelectorAll(SEL.group), function (g) {
      if (!g.querySelector(SEL.input)) return;
      if (g.getAttribute('data-a11y-enhanced') === 'true' && groupFor(g)) return;
      registry.push(Group(g).init());
      added++;
    });

    /* A group appearing or disappearing changes what the shared gate must check.
       Group.init() already syncs new groups, so only removals need a nudge. */
    if (removed && !added && registry.length) registry[0].refreshGate();
    return added;
  }

  function start() {
    boot();
    var root = document.querySelector('.easify-product-options') || document.body;
    new MutationObserver(function () { boot(); }).observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.tpoSwatchA11y = {
    boot: boot,
    groups: function () { return registry; },
    /* QA helper: tpoSwatchA11y.report() shows what each group requires. */
    report: function () {
      return registry.map(function (G) {
        var st = G.requirement();
        return {
          label: st.label,
          selected: st.n,
          min: st.L ? st.L.min : null,
          max: st.L ? st.L.max : null,
          swatches: G.entries().length,
          unit: st.unit,
          ready: st.ready
        };
      });
    },
    version: '1.1'
  };
})();


/* Easify swatch limit guard + Esc "skip to next section"  */
(function () {
  'use strict';

  var CFG = {
    root:         '.easify-product-options',
    group:        '.tpo_option-container',                 // one option (the swatch grid)
    item:         '.tpo_swatches-label_position',          // one swatch card
    swatchInput:  '.tpo_color-swatches-input, .tpo_image-swatches-input, .tpo-a11y-input',
    selected:     '.tpo_quantity_selector_total-selected',
    limit:        '.tpo_quantity_selector_total-limit',
    successClass: 'tpo-quantity-success',                  // app adds this when limit is hit
    plusBtn:      '.tpo_quantity-selector_plus',
    lockedClass:  'tpo-swatch-locked',
    plusLocked:   'tpo-plus-locked',
    lockPlusAtLimit: true                                  // stop stepper from exceeding the limit
  };

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
                  'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  var num = function (t) { var n = parseFloat(String(t || '').replace(/[^\d.-]/g, '')); return isNaN(n) ? 0 : n; };
  var visible = function (el) {
    var r = el.getBoundingClientRect();
    return (r.width > 0 || r.height > 0) && getComputedStyle(el).visibility !== 'hidden';
  };

  /* ---------- styles ---------- */
  if (!document.getElementById('tpo-limit-guard-css')) {
    var css = document.createElement('style');
    css.id = 'tpo-limit-guard-css';
    css.textContent =
      '.' + CFG.lockedClass + '{opacity:.35;filter:grayscale(1);cursor:not-allowed}' +
      '.' + CFG.lockedClass + ' label,.' + CFG.lockedClass + ' .tpo_shape_square,' +
      '.' + CFG.lockedClass + ' .tpo_quantity-selector_wrapper{pointer-events:none!important}' +
      '.' + CFG.plusLocked + '{opacity:.35!important;pointer-events:none!important;cursor:not-allowed}';
    document.head.appendChild(css);
  }

  /* ---------- state ---------- */
  function groups() {
    return [].filter.call(document.querySelectorAll(CFG.group), function (g) {
      return g.querySelector(CFG.selected) && g.querySelector(CFG.swatchInput);
    });
  }
  function state(g) {
    var s = g.querySelector(CFG.selected), l = g.querySelector(CFG.limit);
    var sel = num(s.textContent), lim = num(l && l.textContent);
    return {
      sel: sel, lim: lim,
      reached: s.classList.contains(CFG.successClass) || (lim > 0 && sel >= lim)
    };
  }

  /* ---------- lock / unlock one swatch ---------- */
  function setLocked(input, lock) {
    var item = input.closest(CFG.item) || input.closest('label') || input.parentElement;
    if (lock) {
      if (input.dataset.tpoLocked === '1') return;
      input.dataset.tpoLocked = '1';
      input.disabled = true;                                 // blocks mouse + label click
      input.setAttribute('aria-disabled', 'true');
      if (input.hasAttribute('tabindex')) input.dataset.tpoTab = input.getAttribute('tabindex');
      input.setAttribute('tabindex', '-1');                   // blocks keyboard focus
      if (item) {
        item.classList.add(CFG.lockedClass);
        item.setAttribute('aria-disabled', 'true');
        [].forEach.call(item.querySelectorAll('input,button'), function (el) {
          if (el === input) return;
          if (!el.disabled) el.dataset.tpoWasEnabled = '1';
          el.disabled = true;
          el.setAttribute('tabindex', '-1');
        });
      }
    } else {
      if (input.dataset.tpoLocked !== '1') return;
      delete input.dataset.tpoLocked;
      input.disabled = false;
      input.removeAttribute('aria-disabled');
      if (input.dataset.tpoTab != null) { input.setAttribute('tabindex', input.dataset.tpoTab); delete input.dataset.tpoTab; }
      else input.removeAttribute('tabindex');
      if (item) {
        item.classList.remove(CFG.lockedClass);
        item.removeAttribute('aria-disabled');
        [].forEach.call(item.querySelectorAll('input,button'), function (el) {
          if (el !== input && el.dataset.tpoWasEnabled === '1') {
            el.disabled = false; delete el.dataset.tpoWasEnabled; el.removeAttribute('tabindex');
          }
        });
      }
    }
  }

  /* ---------- main pass (idempotent, safe to run often) ---------- */
  function apply() {
    groups().forEach(function (g) {
      var st = state(g);
      [].forEach.call(g.querySelectorAll(CFG.swatchInput), function (i) {
        setLocked(i, st.reached && !i.checked);   // selected ones stay usable so they can be removed
      });
      if (CFG.lockPlusAtLimit) {
        [].forEach.call(g.querySelectorAll(CFG.plusBtn), function (b) {
          var host = b.closest(CFG.item), cb = host && host.querySelector(CFG.swatchInput);
          var on = st.reached && cb && cb.checked;
          b.classList.toggle(CFG.plusLocked, !!on);
          b.setAttribute('aria-disabled', on ? 'true' : 'false');
        });
      }
    });
  }

  /* ---------- hard guards (app binds its own handlers on the label) ---------- */
  function block(e) {
    var t = e.target;
    if (t && t.closest && (t.closest('.' + CFG.lockedClass) || t.closest('.' + CFG.plusLocked))) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }

  /* ---------- Esc = jump to next section ---------- */
  var lastGroup = null;
  document.addEventListener('focusin', function (e) {
    var g = e.target.closest && e.target.closest(CFG.group);
    if (g) lastGroup = g;
  }, true);
  document.addEventListener('pointerdown', function (e) {
    var g = e.target.closest && e.target.closest(CFG.group);
    if (g) lastGroup = g;
  }, true);

  function goNext(g) {
    var n = g.nextElementSibling, f;
    while (n) {                                  // next option block that has something focusable
      if (n.matches(CFG.group)) {
        f = [].filter.call(n.querySelectorAll(FOCUSABLE), visible)[0];
        if (f) return move(n, f);
      }
      n = n.nextElementSibling;
    }
    var root = g.closest(CFG.root) || g;         // otherwise leave the option set (qty / add-to-cart)
    var after = [].filter.call(document.querySelectorAll(FOCUSABLE), visible).filter(function (el) {
      return !root.contains(el) && (root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
    })[0];
    if (after) move(after.closest('form,section') || after, after);
  }
  function move(section, el) {
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
    (section || el).scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      var g = (e.target.closest && e.target.closest(CFG.group)) || lastGroup;
      if (g && g.querySelector(CFG.swatchInput)) { e.preventDefault(); e.stopPropagation(); goNext(g); }
      return;
    }
    if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') block(e);
  }, true);

  document.addEventListener('mousedown', block, true);
  document.addEventListener('click', block, true);

  /* ---------- keep in sync with the app's re-renders ---------- */
  var raf = null;
  function schedule() { if (raf) return; raf = requestAnimationFrame(function () { raf = null; apply(); }); }
  document.addEventListener('change', schedule, true);
  new MutationObserver(schedule).observe(document.documentElement, {
    subtree: true, childList: true, characterData: true,
    attributes: true, attributeFilter: ['class', 'value', 'checked']
  });
  schedule();
})();









